import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { notification, Select, Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import moment, { Moment } from 'moment';
import axios, { CancelToken } from 'axios';

import DatePicker from 'components/DatePicker/DatePicker';

import { Field, Product, Property, Assignment } from 'base/types/RecommendationCropwise';
import { ProductSeed } from 'base/types/Products';
import barleyDensityService, {
  BarleyDensityField,
  BarleyDensityResponse,
  buildBarleyDensityField,
} from 'base/api/barleyDensityService';
import {
  Coordinates,
  calculateFieldsCentroid,
  formatBarleyVarietyName,
  getBarleyYieldLevel,
  isBarleyCrop,
  isBarleyDensitySupportedCountry,
  supportsGrassWeedPressure,
} from 'utils/barleyDensityHelpers';
import { useAppState } from 'context/AppState';
import { useBreakpoint } from 'hooks';

import {
  CardStyled,
  ConditionDropdown,
  Container,
  ControlField,
  ControlsRow,
  EmptyAdjustedOverlay,
  ErrorBanner,
  HeaderRow,
  InfoIcon,
  MobileTableWrapper,
  NoWrapFieldLabel,
  SectionContent,
  Subtitle,
  TableWrapper,
  Title,
} from './BarleyPlantDensity.styles';
import { SkeletonRowStyled } from '../SoilCharacteristics.styles';

export interface BarleyPlantDensityProps {
  recommendationCropwise?: any;
  sectionRef?: any;
}

type GrowingCondition = 'Bad' | 'Neutral' | 'Good';

const GROWING_CONDITIONS: GrowingCondition[] = ['Bad', 'Neutral', 'Good'];

/** UI label -> API enum value (`planting_condition`). */
const GROWING_CONDITION_TO_API: Record<GrowingCondition, string> = {
  Bad: 'Bad conditions',
  Neutral: 'Normal conditions',
  Good: 'Good conditions',
};

const REQUEST_VERSION = 'v1.0';
const ISO_DATE_FORMAT = 'YYYY-MM-DD';

interface VarietyRow {
  productName: string;
  displayName: string;
  recommendedSeeds?: number; // plants/ha from baseline call
  adjustedSeedRate?: number; // plants/ha from user-customized call
  unsupported?: boolean; // true when the variety is not supported by the density API
}

const collectAllFields = (properties?: Property[]): Field[] => {
  if (!properties) {
    return [];
  }
  return properties.flatMap((p) => p?.fields || []);
};

const findPlantsPerHa = (
  features: { type: string; value: number }[] | undefined
): number | undefined => features?.find((f) => f?.type === 'density_plants_per_ha')?.value;

// Rounds to the nearest 10: x1-x4 rounds down, x5-x9 rounds up (e.g. 61-64 -> 60, 65-69 -> 70).
const roundToNearestTen = (value: number): number => Math.round(value / 10) * 10;

const formatAdjustedSeedRate = (value?: number): string => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '-';
  }
  // Convert seeds/ha to seeds/m² (1 ha = 10,000 m²), then round to the nearest 10
  const seedsPerSqm = Math.round(value / 10000);
  return `${roundToNearestTen(seedsPerSqm).toLocaleString()}`;
};

/** Maps the tri-state grass-weed rule onto the Select's option value. */
const toGrassWeedSelectValue = (grassWeedRule?: boolean): 'Yes' | 'No' | undefined => {
  if (grassWeedRule === undefined) {
    return undefined;
  }
  return grassWeedRule ? 'Yes' : 'No';
};

/** Copies the density API's plants/ha onto each row, matched positionally. */
const applyAdjustedRates = (rows: VarietyRow[], response: BarleyDensityResponse): VarietyRow[] =>
  rows.map((row, idx) => ({
    ...row,
    adjustedSeedRate: findPlantsPerHa(response?.results?.[idx]?.predictions?.[0]?.features),
  }));

/** Flags the rows whose variety the density API rejected as unsupported. */
const markUnsupportedRows = (rows: VarietyRow[], unsupportedSet: Set<string>): VarietyRow[] =>
  rows.map((row) => ({
    ...row,
    unsupported: unsupportedSet.has(formatBarleyVarietyName(row.productName).toLowerCase()),
  }));

/** Indexes a retry response by variety name, since the retry drops unsupported fields. */
const buildVarietyResultMap = (
  fields: BarleyDensityField[],
  response: BarleyDensityResponse
): Map<string, BarleyDensityResponse['results'][0]> => {
  const resultMap = new Map<string, BarleyDensityResponse['results'][0]>();
  fields.forEach((field, idx) => {
    if (response?.results?.[idx]) {
      resultMap.set(field.variety.toLowerCase(), response.results[idx]);
    }
  });
  return resultMap;
};

/** Applies a retry response to the supported rows, leaving unsupported ones untouched. */
const applyRetriedRates = (
  rows: VarietyRow[],
  resultMap: Map<string, BarleyDensityResponse['results'][0]>
): VarietyRow[] =>
  rows.map((row) => {
    if (row.unsupported) {
      return row;
    }
    const result = resultMap.get(formatBarleyVarietyName(row.productName).toLowerCase());
    return {
      ...row,
      adjustedSeedRate: findPlantsPerHa(result?.predictions?.[0]?.features),
    };
  });

const EmptyTableIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="49"
    height="44"
    viewBox="0 0 49 44"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M27.6012 19.5084C28.0633 28.2347 25.7326 37.9081 25.0177 39.997C23.1811 40.2427 22.3434 39.7249 22.3434 39.7249C22.3434 39.7249 24.4878 33.4581 25.4567 25.7751C26.4301 18.0567 25.7476 8.54905 24.789 2.60765C24.5245 1.27541 24.3938 0.600615 24.514 1.03873C24.6073 1.5276 24.6994 2.05203 24.789 2.60765C25.545 6.41532 27.3939 15.5934 27.6012 19.5084Z"
      fill="#00004B"
    />
    <path
      d="M19.2565 18.8754C21.741 27.4255 22.051 37.8412 21.9361 40.1601C20.4159 41.0109 19.5718 40.7707 19.5718 40.7707C19.5718 40.7707 19.9164 33.8138 18.9117 25.8322C17.9023 17.8138 15.0415 8.55708 12.7962 2.94798C12.2501 1.7066 11.9763 1.07676 12.1844 1.47405C12.3816 1.9308 12.5862 2.42344 12.7962 2.94798C14.3569 6.49596 18.1418 15.0394 19.2565 18.8754Z"
      fill="#73DC78"
    />
    <path
      d="M25.902 18.262C23.0508 27.0471 22.3331 37.4047 22.3599 39.6874C23.8691 40.3374 24.7346 39.9924 24.7346 39.9924C24.7346 39.9924 24.6543 33.1443 25.9825 25.11C27.3167 17.0386 30.5771 7.50575 33.0718 1.66444C33.6738 0.365434 33.9759 -0.293236 33.7494 0.126728C33.5317 0.604206 33.3051 1.11818 33.0718 1.66444C31.3513 5.37713 27.1812 14.3205 25.902 18.262Z"
      fill="#73DC78"
    />
    <path
      d="M19.7398 43.7212L4.20144 43.7036C2.14256 43.7013 0.652071 41.6587 1.30057 39.7046C3.06824 34.3782 5.66956 28.151 11.1862 28.7686C15.5028 29.2519 19.7616 35.0454 22.2644 39.8411C23.2246 41.6811 21.8153 43.7236 19.7398 43.7212Z"
      fill="#A3A9B9"
    />
    <path
      d="M43.9277 43.7223L21.9811 43.7062C19.7207 43.7045 18.1765 41.2492 19.2481 39.259C21.5973 34.8958 25.076 30.2653 31.9149 30.7629C37.3252 31.1567 42.6674 35.6172 46.0607 39.6031C47.5396 41.3404 46.2091 43.724 43.9277 43.7223Z"
      fill="#696F88"
    />
  </svg>
);

const ChevronDown = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M2.5 4.5L6 8L9.5 4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const UnfoldMoreIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 3.75L12 6.75H6L9 3.75ZM9 14.25L6 11.25H12L9 14.25Z" fill="currentColor" />
  </svg>
);

const BarleyPlantDensity = ({ recommendationCropwise, sectionRef }: BarleyPlantDensityProps) => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const {
    apiData: { countryList, productCatalog },
  } = useAppState();

  const recommendation = recommendationCropwise?.recommendations?.[0];
  const cropName: string = recommendation?.crop_name ?? '';
  const country: string = (recommendationCropwise?.country ?? '').trim();
  const isDDG = recommendationCropwise?.source === 'DDG';

  const isVisible = isBarleyCrop(cropName) && isBarleyDensitySupportedCountry(isDDG, country);

  const products: Product[] = useMemo(() => recommendation?.products ?? [], [recommendation]);

  const properties: Property[] | undefined = recommendation?.multi_field?.geometry?.properties;
  const allFields = useMemo(() => collectAllFields(properties), [properties]);

  const centroid: Coordinates | null = useMemo(
    () => calculateFieldsCentroid(allFields),
    [allFields]
  );

  const agronomicInputs = recommendation?.multi_field?.config?.agronomicInputs;
  const defaultPlantingDate: string = agronomicInputs?.plantingDate || '';
  const yieldRange = agronomicInputs?.yieldRange || recommendation?.multi_field?.config?.yieldRange;

  const yieldLevel = useMemo(
    () => (isVisible ? getBarleyYieldLevel(country, yieldRange, countryList) : undefined),
    [isVisible, country, yieldRange, countryList]
  );

  const showGrassWeedControl = supportsGrassWeedPressure(country);

  // Controls — start unselected per Figma ("Select" placeholder)
  const [plantingDate, setPlantingDate] = useState<Moment | null>(
    defaultPlantingDate ? moment(defaultPlantingDate) : null
  );
  const [growingCondition, setGrowingCondition] = useState<GrowingCondition | undefined>();
  const [conditionDropdownOpen, setConditionDropdownOpen] = useState(false);
  const [grassWeedRule, setGrassWeedRule] = useState<boolean | undefined>();

  // Async state
  const [isLoadingAdjusted, setIsLoadingAdjusted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<VarietyRow[]>([]);

  // Re-sync date when default changes (e.g. after recommendation loads)
  useEffect(() => {
    if (defaultPlantingDate) {
      setPlantingDate(moment(defaultPlantingDate));
    }
  }, [defaultPlantingDate]);

  // Initialize visible product rows with seed_rate from recommendation assignments.
  useEffect(() => {
    if (!isVisible) {
      return;
    }
    const assignments: Assignment[] = recommendation?.multi_field?.assignments ?? [];
    setRows(
      products.map((p) => {
        // Find seed_rate for this product across all field assignments, take the first match
        let seedRate: number | undefined;
        for (const assignment of assignments) {
          const match = assignment.products?.find((ap) => ap.product_id === p.product_id);
          if (match?.seed_rate !== undefined) {
            seedRate = match.seed_rate;
            break;
          }
        }
        // Fallback: alternate products are not in assignments — use defaultSeedingRate from catalog
        if (seedRate === undefined) {
          const catalogProduct = ((productCatalog as ProductSeed[]) ?? []).find(
            (cp) => cp.id === p.product_id
          );
          const defaultRate = Number(catalogProduct?.defaultSeedingRate);
          if (defaultRate) {
            seedRate = defaultRate;
          }
        }
        return {
          productName: p.product_name,
          displayName: p.commercial_name || formatBarleyVarietyName(p.product_name),
          recommendedSeeds: seedRate,
        };
      })
    );
  }, [isVisible, products, recommendation, productCatalog]);

  const buildPayload = useCallback(
    (
      plantingDateStr: string,
      conditionApi: string,
      grassWeed: boolean | undefined
    ): BarleyDensityField[] => {
      if (!centroid || !yieldLevel) {
        return [];
      }
      return products.map((product) =>
        buildBarleyDensityField({
          centroid,
          plantingDate: plantingDateStr,
          variety: formatBarleyVarietyName(product.product_name),
          yieldLevel,
          plantingCondition: conditionApi,
          grassWeedPressure: showGrassWeedControl ? !!grassWeed : undefined,
        })
      );
    },
    [centroid, products, showGrassWeedControl, yieldLevel]
  );

  const handleApiError = useCallback(
    (err: any) => {
      const message =
        err?.response?.status === 422
          ? t('Some inputs are not supported for this region. Please review your selections.')
          : t('Failed to fetch seed rate recommendations.');
      setError(message);
      notification.error({
        message: t('Seed Rate Explorer'),
        description: message,
        placement: 'topRight',
      });
    },
    [t]
  );

  /**
   * Parse a 422 error response and extract unsupported variety names.
   * The API returns: { detail: [{ loc: [..., "variety"], input: "SY Moovy", ... }] }
   */
  const extractUnsupportedVarieties = (err: any): string[] => {
    const details = err?.response?.data?.detail;
    if (!Array.isArray(details)) {
      return [];
    }
    return details
      .filter(
        (d: any) => Array.isArray(d.loc) && d.loc.includes('variety') && typeof d.input === 'string'
      )
      .map((d: any) => d.input as string);
  };

  // Second density call, issued when the first one rejected some varieties (422).
  const retryWithSupportedVarieties = useCallback(
    (fields: BarleyDensityField[], cancelToken?: CancelToken) => {
      barleyDensityService
        .fetchBarleyDensity({ request_version: REQUEST_VERSION, fields }, cancelToken)
        .then((response: BarleyDensityResponse) => {
          const resultMap = buildVarietyResultMap(fields, response);
          setRows((prev) => applyRetriedRates(prev, resultMap));
        })
        .catch((retryErr) => {
          if (!axios.isCancel(retryErr)) {
            handleApiError(retryErr);
          }
        })
        .finally(() => setIsLoadingAdjusted(false));
    },
    [handleApiError]
  );

  // Adjusted call: fires whenever the user picks Growing Conditions
  // (and Grass Weed Rule, if shown). Re-fires on any subsequent change
  // including planting date.
  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }
    if (!plantingDate || !yieldLevel || !centroid || products.length === 0) {
      return undefined;
    }
    if (!growingCondition) {
      return undefined;
    }
    if (showGrassWeedControl && grassWeedRule === undefined) {
      return undefined;
    }

    const formattedDate = plantingDate.format(ISO_DATE_FORMAT);
    const payload = buildPayload(
      formattedDate,
      GROWING_CONDITION_TO_API[growingCondition],
      grassWeedRule
    );
    if (payload.length === 0) {
      return undefined;
    }

    setIsLoadingAdjusted(true);
    setError(null);
    // Reset unsupported flags before new call
    setRows((prev) =>
      prev.map((row) => ({ ...row, unsupported: false, adjustedSeedRate: undefined }))
    );

    const cancelSource = barleyDensityService.cancelToken();

    barleyDensityService
      .fetchBarleyDensity({ request_version: REQUEST_VERSION, fields: payload }, cancelSource.token)
      .then((response: BarleyDensityResponse) => {
        setRows((prev) => applyAdjustedRates(prev, response));
        setIsLoadingAdjusted(false);
      })
      .catch((err) => {
        if (axios.isCancel(err)) {
          return;
        }
        if (err?.response?.status === 422) {
          const unsupportedNames = extractUnsupportedVarieties(err);
          if (unsupportedNames.length > 0) {
            // Mark unsupported rows
            const unsupportedSet = new Set(unsupportedNames.map((n) => n.toLowerCase()));
            setRows((prev) => markUnsupportedRows(prev, unsupportedSet));

            // Build a filtered payload excluding unsupported varieties
            const filteredPayload = payload.filter(
              (f) => !unsupportedSet.has(f.variety.toLowerCase())
            );
            if (filteredPayload.length > 0) {
              // Retry with supported varieties only
              retryWithSupportedVarieties(filteredPayload, cancelSource.token);
              return;
            }
          }
        }
        handleApiError(err);
        setIsLoadingAdjusted(false);
      });

    return () => {
      cancelSource.cancel();
    };
  }, [
    isVisible,
    plantingDate,
    yieldLevel,
    centroid,
    products.length,
    growingCondition,
    grassWeedRule,
    showGrassWeedControl,
    buildPayload,
    handleApiError,
    retryWithSupportedVarieties,
  ]);

  if (!isVisible) {
    return null;
  }

  const hasAdjusted = rows.some((r) => r.adjustedSeedRate !== undefined);
  const isLoading = isLoadingAdjusted;

  const renderAdjustedSeedRateCell = (row: VarietyRow) => {
    if (isLoading) {
      return <SkeletonRowStyled width="100%" />;
    }
    if (row.unsupported) {
      return <span className="unsupported-cell">{t('Data not available for this product')}</span>;
    }
    if (row.adjustedSeedRate === undefined) {
      return <span className="empty-cell">&nbsp;</span>;
    }
    return formatAdjustedSeedRate(row.adjustedSeedRate);
  };

  return (
    <Container ref={sectionRef} data-testid="barley-plant-density">
      <HeaderRow>
        <Title level={2}>{t('Seed Rate Explorer')}</Title>
        <Subtitle>
          {t(
            'Fill in the information below to check the planting conditions according to your yield capacity on your farm.'
          )}
        </Subtitle>
      </HeaderRow>

      <CardStyled>
        <SectionContent>
          <ControlsRow>
            <ControlField>
              <label className="field-label">{t('Planting date')}</label>
              <DatePicker
                data-testid="barley-planting-date-input"
                value={plantingDate}
                defaultValue={plantingDate || undefined}
                allowClear={false}
                onChange={(date: Moment | null) => setPlantingDate(date)}
              />
            </ControlField>

            <ControlField>
              <span className="field-label">
                {t('Growing Conditions')}
                <Tooltip
                  title={t('Growing Conditions Tooltip Description')}
                  trigger={isMobile ? ['click'] : ['hover']}
                  placement={isMobile ? 'bottom' : 'top'}
                  getPopupContainer={(triggerNode) => triggerNode.parentElement as HTMLElement}
                >
                  <InfoIcon aria-label={t('More information')}>i</InfoIcon>
                </Tooltip>
                {''}*
              </span>
              <Select
                id="barley-growing-condition"
                placeholder={t('Select')}
                value={growingCondition ? t(growingCondition) : t('Select')}
                open={conditionDropdownOpen}
                onDropdownVisibleChange={setConditionDropdownOpen}
                onChange={(val) => setGrowingCondition(val as GrowingCondition)}
                suffixIcon={<ChevronDown />}
                dropdownRender={() => (
                  <ConditionDropdown>
                    <div className="dropdown-header">{t('Select condition')}</div>
                    {GROWING_CONDITIONS.map((c) => (
                      <div
                        key={c}
                        role="option"
                        tabIndex={0}
                        aria-selected={growingCondition === c}
                        className={`dropdown-option${growingCondition === c ? ' selected' : ''}`}
                        onClick={() => {
                          setGrowingCondition(c);
                          setConditionDropdownOpen(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setGrowingCondition(c);
                            setConditionDropdownOpen(false);
                          }
                        }}
                      >
                        {t(c)}
                      </div>
                    ))}
                  </ConditionDropdown>
                )}
                getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
              />
            </ControlField>

            {showGrassWeedControl && (
              <ControlField>
                <NoWrapFieldLabel>{`${t('Apply Grass Weed Rule')}?`} *</NoWrapFieldLabel>
                <Select
                  id="barley-grass-weed"
                  placeholder={t('Select')}
                  value={toGrassWeedSelectValue(grassWeedRule)}
                  onChange={(val) => setGrassWeedRule(val === 'Yes')}
                  suffixIcon={<ChevronDown />}
                  options={[
                    { label: t('No'), value: 'No' },
                    { label: t('Yes'), value: 'Yes' },
                  ]}
                  getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                />
              </ControlField>
            )}
          </ControlsRow>

          {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

          {isMobile ? (
            <MobileTableWrapper>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: hasAdjusted ? '50%' : '100%' }}>{t('Product Name')}</th>
                    <th style={{ width: '50%' }}>{t('Adjusted Seed Rate')}</th>
                  </tr>
                </thead>
              </table>
              <div style={{ display: 'flex' }}>
                <table style={{ width: hasAdjusted ? '100%' : '50%' }}>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.productName}>
                        <td style={{ width: hasAdjusted ? '50%' : '100%' }}>{row.displayName}</td>
                        {hasAdjusted && (
                          <td style={{ width: '50%' }}>
                            {/* formatAdjustedSeedRate already renders '-' for a missing rate */}
                            {row.unsupported ? '-' : formatAdjustedSeedRate(row.adjustedSeedRate)}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!hasAdjusted && rows.length > 0 && (
                  <div className="mobile-empty-overlay" style={{ width: '50%' }}>
                    <EmptyTableIcon />
                    <span className="mobile-empty-text">
                      {t('Select growing conditions to view data')}
                    </span>
                  </div>
                )}
              </div>
            </MobileTableWrapper>
          ) : (
            <TableWrapper aria-busy={isLoading}>
              <table>
                <thead>
                  <tr>
                    <th>
                      <div className="th-content">
                        {t('Product Name')}
                        <button type="button" className="sort-btn" aria-label={t('Sort')}>
                          <UnfoldMoreIcon />
                        </button>
                      </div>
                    </th>
                    <th>
                      {t('Adjusted Seed Rate')} ({t('seeds per square meter')})
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.productName}>
                      <td className="product-name">{row.displayName}</td>
                      <td>{renderAdjustedSeedRateCell(row)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {!hasAdjusted && rows.length > 0 && !isLoading && (
                <EmptyAdjustedOverlay>
                  <EmptyTableIcon />
                  <span>{t('Select growing conditions to view data')}</span>
                </EmptyAdjustedOverlay>
              )}
            </TableWrapper>
          )}
        </SectionContent>
      </CardStyled>
    </Container>
  );
};

export default BarleyPlantDensity;
